import { FC, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'

import PerText from '../Text'
import PerButton from '../Button'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { ObjectiveInput, ObjectiveStatus } from '../../../types'
import { useTargetsModalStore } from './TargetsModalStore'
import { transformEmployeeTargetBeforeSave } from './util'
import { TargetModalType, ObjectiveExtended, TargetModalTargetType } from './types'
import {
  targetModalSaveTargetInMeeting,
  targetModalSaveTarget,
  targetModalDuplicateAndDismissOrDeleteInMeeting,
  deleteObjectiveApi,
} from './async'
import { nanoid } from 'nanoid'
import { PerModal } from '../Modal'
import { showNotification } from '../Notification/notification'

export const TargetsModalFooter: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateToUtcString } = useDateTime(user)
  const [isProcessing, toggleProcessing] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isDisclaimerOpened, toggleDisclaimerOpened] = useState(false)

  const [
    closeModal,
    cleanModal,
    newTargetForm,
    modalProps,
    currentObjective,
    currentComments,
    commentFromInput,
    currentAttachment,
    targetModalType,
    currencyConversionRate,
  ] = useTargetsModalStore(
    (state) => [
      state.closeTargetModal,
      state.cleanTargetModal,
      state.newTargetForm,
      state.modalProps,
      state.currentObjective,
      state.currentComments,
      state.commentFromInput,
      state.currentAttachment,
      state.targetModalType,
      state.currencyConversionRate,
    ],
    shallow,
  )

  const handleClose = () => {
    closeModal()
    setTimeout(() => {
      cleanModal()
    }, 300)
  }

  const getNotificationType = (): string => {
    if ([TargetModalType.viewTarget, TargetModalType.viewTargetInMeeting, TargetModalType.deleteTarget].includes(targetModalType)) {
      return t('targetModal.target')
    } else if (
      [
        TargetModalType.appraisalNewTarget,
        TargetModalType.appraisalEditActiveTarget,
        TargetModalType.appraisalSuggestedTarget,
        TargetModalType.duplicateAndDismissOrDeleteTarget,
      ].includes(targetModalType)
    ) {
      return t('targetModal.newTarget')
    } else if (TargetModalType.appraisalNewPastTarget === targetModalType) {
      return t('targetModal.newPastTarget')
    } else if ([TargetModalType.appraisalEditPastTarget, TargetModalType.duplicateAndDismissOrDeletePastTarget].includes(targetModalType)) {
      return t('targetModal.pastTarget')
    } else {
      return ''
    }
  }

  const getNotificationOperation = (): string => {
    if (targetModalType === TargetModalType.deleteTarget) {
      return t('targetModal.notifications.operations.deleted')
    }
    if (
      [TargetModalType.duplicateAndDismissOrDeleteTarget, TargetModalType.duplicateAndDismissOrDeletePastTarget].includes(targetModalType)
    ) {
      return t('targetModal.notifications.operations.duplicatedAndDismissed')
    } else if (!!newTargetForm && newTargetForm.getValues().id !== undefined) {
      return t('targetModal.notifications.operations.edited')
    } else if (TargetModalType.appraisalSuggestedTarget === targetModalType) {
      return t('targetModal.notifications.operations.suggested')
    } else {
      return t('targetModal.notifications.operations.created')
    }
  }

  const showToaster = () => {
    const type: string = getNotificationType()
    const operation: string = getNotificationOperation()
    showNotification({
      type: 'success',
      message: t('targetModal.notifications.success.title'),
      description: t('targetModal.notifications.success.description', { type, operation }),
    })
  }

  const isTargetClosedOrDismissed =
    !!currentObjective &&
    [ObjectiveStatus.Closed, ObjectiveStatus.Dismissed, ObjectiveStatus.DismissedPending].includes(currentObjective.status)
  const isResignedEmployeeView = targetModalType === TargetModalType.resignedEmployeViewTarget

  const saveTarget = async () => {
    if (!modalProps.employeeId) {
      setErrorMessage(t('targetModal.userNotFound'))
      toggleProcessing(false)
    } else {
      if (!!newTargetForm) {
        const _commentFromInput = !!commentFromInput
          ? {
              id: nanoid(),
              text: commentFromInput,
              date: formatDateToUtcString(new Date()),
              isNewComment: false,
              author: { id: user.employeeId, firstName: user.firstName, lastName: user.lastName, avatarUrl: user.avatarUrl },
            }
          : undefined

        const { achievedAmountPercentage, markedAsDone, ...formValues } = newTargetForm.getValues() as ObjectiveExtended
        const { errors } = await targetModalSaveTarget(
          modalProps.employeeId,
          transformEmployeeTargetBeforeSave(formValues, currentAttachment, currentComments, _commentFromInput),
        )
        if (!!errors) {
          setErrorMessage(t('targetModal.targetError'))
          toggleProcessing(false)
        } else {
          showToaster()
          handleClose()
        }
      }
    }
  }

  const saveTargetInMeeting = async () => {
    if (!modalProps.meetingId) {
      setErrorMessage(t('targetModal.meetingNotFound'))
      toggleProcessing(false)
    } else {
      if (!!newTargetForm) {
        const { targetType, achievedAmountPercentage, markedAsDone, ...formValues } = newTargetForm.getValues() as ObjectiveExtended

        let values: ObjectiveInput = { ...formValues }
        const _commentFromInput = !!commentFromInput
          ? {
              id: nanoid(),
              text: commentFromInput,
              date: formatDateToUtcString(new Date()),
              isNewComment: false,
              author: { id: user.employeeId, firstName: user.firstName, lastName: user.lastName, avatarUrl: user.avatarUrl },
            }
          : undefined

        if (targetModalType === TargetModalType.appraisalNewTarget || targetModalType === TargetModalType.appraisalNewPastTarget) {
          const { targetAmount, achievedAmount, ...rest } = values
          values = {
            ...rest,
            targetAmount: targetType === TargetModalTargetType.monetary ? targetAmount : null,
            achievedAmount: targetType === TargetModalTargetType.monetary && !!markedAsDone ? achievedAmount : null,
            // new Past Target gets directly status "PastApproved"
            // new New Target (both in A and AU) when created can be marked as done so they get "Approved" status if monetary or "MarkedAsDone" if development target,
            // if not marked as done then they get "ProposalFromManager" status

            status:
              targetModalType === TargetModalType.appraisalNewPastTarget
                ? ObjectiveStatus.PastApproved
                : targetType === TargetModalTargetType.monetary
                ? markedAsDone
                  ? ObjectiveStatus.Approved
                  : ObjectiveStatus.ProposalFromManager
                : markedAsDone
                ? ObjectiveStatus.MarkedAsDone
                : ObjectiveStatus.ProposalFromManager,
            currency: targetType === TargetModalTargetType.monetary ? modalProps.employeeCurrency : undefined,
          }
        }

        if (targetModalType === TargetModalType.appraisalEditActiveTarget || targetModalType === TargetModalType.appraisalEditPastTarget) {
          const isMonetary = !Number.isNaN(Number(values.targetAmount || Number.NaN))
          if (isMonetary) {
            values = { ...values, achievedAmount: !!markedAsDone ? values.achievedAmount : null }
          } else {
            values = {
              ...values,
              status: !!markedAsDone
                ? ObjectiveStatus.MarkedAsDone
                : targetModalType === TargetModalType.appraisalEditPastTarget && !values.id
                ? ObjectiveStatus.PastApproved
                : ObjectiveStatus.Approved,
            }
          }
        }

        const { errors } = await targetModalSaveTargetInMeeting(
          modalProps.meetingId,
          transformEmployeeTargetBeforeSave(values, currentAttachment, currentComments, _commentFromInput),
        )
        if (!!errors) {
          setErrorMessage(t('targetModal.targetError'))
          toggleProcessing(false)
        } else {
          showToaster()
          handleClose()
        }
      }
    }
  }

  const deleteTarget = async () => {
    if (!modalProps.meetingId) {
      setErrorMessage(t('targetModal.meetingNotFound'))
      toggleProcessing(false)
    } else if (!currentObjective) {
      setErrorMessage(t('targetModal.currentObjectiveNotFound'))
      toggleProcessing(false)
    } else if (!currentObjective.globalId) {
      setErrorMessage(t('targetModal.globalIdNotFound'))
      toggleProcessing(false)
    } else if (targetModalType !== TargetModalType.deleteTarget) {
      setErrorMessage(t('targetModal.deleteNotAllowed'))
      toggleProcessing(false)
    } else {
      const { errors } = await deleteObjectiveApi(currentObjective.globalId, modalProps.meetingId)
      if (!!errors && !!errorMessage.length) {
        setErrorMessage(t('targetModal.targetError'))
        toggleProcessing(false)
      } else {
        showToaster()
        handleClose()
      }
    }
  }

  const duplicateAndDismissOrDeleteTarget = async () => {
    if (!modalProps.meetingId) {
      setErrorMessage(t('targetModal.meetingNotFound'))
      toggleProcessing(false)
    } else if (!currentObjective) {
      setErrorMessage(t('targetModal.currentObjectiveNotFound'))
      toggleProcessing(false)
    } else if (!currentObjective.globalId) {
      setErrorMessage(t('targetModal.globalIdNotFound'))
      toggleProcessing(false)
    } else if (
      ![TargetModalType.duplicateAndDismissOrDeleteTarget, TargetModalType.duplicateAndDismissOrDeletePastTarget].includes(targetModalType)
    ) {
      setErrorMessage(t('targetModal.duplicationAndDismissionNotAllowed'))
      toggleProcessing(false)
    } else {
      const newObjective = { ...currentObjective }
      if (
        targetModalType === TargetModalType.duplicateAndDismissOrDeleteTarget &&
        [ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone].includes(currentObjective.status)
      ) {
        newObjective.status = ObjectiveStatus.ProposalFromManager
      } else if (
        targetModalType === TargetModalType.duplicateAndDismissOrDeletePastTarget &&
        currentObjective.status === ObjectiveStatus.Approved
      ) {
        newObjective.status = ObjectiveStatus.PastApproved
      }
      const { errors } = await targetModalDuplicateAndDismissOrDeleteInMeeting(newObjective, modalProps.meetingId)
      if (!!errors) {
        setErrorMessage(t('targetModal.targetError'))
        toggleProcessing(false)
      } else {
        showToaster()
        handleClose()
      }
    }
  }

  const handleSubmit = async () => {
    if (!!newTargetForm && !!newTargetForm.trigger) {
      await newTargetForm.trigger()
      if (Object.keys(newTargetForm.formState.errors).length) {
        setErrorMessage(t('targetModal.formErrors'))
      } else {
        toggleProcessing(true)
        const { targetAmount, achievedAmount, markedAsDone } = newTargetForm.getValues() as ObjectiveExtended
        if (targetModalType === TargetModalType.viewTarget) {
          await saveTarget()
        } else if (
          [TargetModalType.duplicateAndDismissOrDeleteTarget, TargetModalType.duplicateAndDismissOrDeletePastTarget].includes(
            targetModalType,
          )
        ) {
          await duplicateAndDismissOrDeleteTarget()
        } else if (
          targetModalType === TargetModalType.appraisalNewTarget &&
          targetAmount !== undefined &&
          markedAsDone &&
          achievedAmount !== undefined
        ) {
          toggleDisclaimerOpened(true)
        } else if (targetModalType === TargetModalType.deleteTarget) {
          await deleteTarget()
        } else {
          await saveTargetInMeeting()
        }
      }
    }
  }

  const CloseTargetDisclaimer = ({ isDisclaimerOpened, handleProceed, handleCancel }) => {
    const [t] = useTranslation()

    return (
      <PerModal
        zIndex={2000}
        centered
        onCancel={handleCancel}
        visible={isDisclaimerOpened}
        title={{
          heading: t('targetModal.closeNewMonetaryTargetDisclaimer.heading'),
        }}
        footer={{
          confirmBtn: { onClick: handleProceed },
          cancelBtn: { onClick: handleCancel },
        }}
      >
        <span style={{ whiteSpace: 'pre-line' }}>
          <Trans i18nKey="targetModal.closeNewMonetaryTargetDisclaimer.body" values={{ year: modalProps.meeting?.appraisalSeason.year }} />
        </span>
      </PerModal>
    )
  }

  return (
    <>
      {isDisclaimerOpened && (
        <CloseTargetDisclaimer
          isDisclaimerOpened={isDisclaimerOpened}
          handleProceed={async () => {
            toggleDisclaimerOpened(false)
            await saveTargetInMeeting()
          }}
          handleCancel={() => {
            toggleProcessing(false)
            toggleDisclaimerOpened(false)
          }}
        />
      )}
      <PerFlexRow align="middle" justify="space-between">
        <PerFlexRow>
          <PerButton
            text={t('global.button.cancel')}
            color="secondary"
            before="close_stroke"
            variant="pastel"
            type="button"
            onClick={handleClose}
          />
        </PerFlexRow>
        <PerFlexRow align="middle">
          {!!errorMessage && (
            <PerText appearance="info" color="error">
              {errorMessage}
            </PerText>
          )}
          <PerSpacer ml="xxxs" mr="xxxs" />
          {!isTargetClosedOrDismissed && !isResignedEmployeeView && (
            <PerButton
              text={
                targetModalType === TargetModalType.deleteTarget
                  ? t('global.button.confirm')
                  : ![TargetModalType.duplicateAndDismissOrDeleteTarget, TargetModalType.duplicateAndDismissOrDeletePastTarget].includes(
                      targetModalType,
                    )
                  ? t('global.button.save')
                  : !!currentObjective && (currentObjective.id === undefined || currentObjective.id === null)
                  ? t('global.button.duplicateAndDelete')
                  : t('global.button.duplicateAndDismiss')
              }
              color="success"
              before="check_stroke"
              variant="pastel"
              type="button"
              disabled={
                [TargetModalType.duplicateAndDismissOrDeleteTarget, TargetModalType.duplicateAndDismissOrDeletePastTarget].includes(
                  targetModalType,
                ) &&
                (!currentObjective || currencyConversionRate === undefined)
              }
              isProcessing={{ active: isProcessing }}
              onClick={handleSubmit}
              data-e2e-targets-modal-button-confirm
              tooltip={
                ![TargetModalType.duplicateAndDismissOrDeleteTarget, TargetModalType.duplicateAndDismissOrDeletePastTarget].includes(
                  targetModalType,
                )
                  ? undefined
                  : !!currentObjective && currencyConversionRate === undefined
                  ? t('currencyConversion.tooltips.tableConversionWarning', {
                      startingCurrency: currentObjective.currency,
                      targetCurrency: modalProps.employeeCurrency,
                    })
                  : undefined
              }
            />
          )}
        </PerFlexRow>
      </PerFlexRow>
    </>
  )
}
